import { useQuery } from '@tanstack/react-query';
import { useHybridAuth } from './useHybridAuth';
import { useDriverProfile } from './useDriverProfile';
import { driverApi } from '../services/driverApi';
import { companyIdFromProfileOrStorage } from './userCompanies.shared';
import type { PermissionKey, UserAccessDto } from '@relay-towing/api';

export type DriverAccess = UserAccessDto;

/**
 * The caller's Roles page checkboxes, which also decide the tabs
 * (useDriverTabs). A failure is an error, not an empty answer: the request used to
 * catch everything and return no permissions, which React Query cached as
 * a success, so one dropped request hid every control for five minutes and
 * told the driver they lacked permissions they held.
 */
const useDriverAccess = () => {
  const { getAccessTokenSilently, isAuthenticated } = useHybridAuth();
  const { data: profile } = useDriverProfile();
  // An owner or admin with no Driver row gets a placeholder profile whose
  // companyId is ''; asking with that sent an empty x-company-id.
  const companyId = companyIdFromProfileOrStorage(profile);

  return useQuery({
    queryFn: async (): Promise<DriverAccess> => {
      if (!profile?.userId) throw new Error('User ID not available in profile');
      const token = await getAccessTokenSilently();
      driverApi.setAuthToken(token);
      return driverApi.getPermissions(profile.userId, companyId);
    },
    queryKey: ['driver-permissions', profile?.userId, companyId],
    enabled: isAuthenticated && !!profile?.userId && !!companyId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export interface DriverPermissionsState {
  permissions: PermissionKey[];
  /**
   * True until the permissions have been answered. React Query's own
   * isLoading is false while the query waits for the profile, which would
   * let a "you need this permission" message flash before the answer.
   */
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
}

/**
 * Whether the permissions are known, for a screen that says what the
 * caller cannot do. Say it only once `isLoading` and `isError` are both
 * false; on an error, offer `refetch` rather than claiming the key is
 * missing.
 */
export const useDriverPermissions = (): DriverPermissionsState => {
  const query = useDriverAccess();
  return {
    permissions: query.data?.permissions ?? [],
    isLoading: query.isPending,
    isError: query.isError,
    refetch: () => void query.refetch(),
  };
};

/**
 * Whether the caller holds a Roles page checkbox, for deciding what to show.
 * Takes the generated PermissionKey, so a key that does not exist is a type
 * error: this app checked `drivers.edit_status` for years before it existed.
 * The backend enforces every key itself and scopes lists by them; never
 * filter rows here.
 *
 * False until the answer arrives, which hides a control; that is safe. A
 * message saying the key is missing must also wait for useDriverPermissions
 * to finish loading.
 */
export const useHasPermission = (permissionKey: PermissionKey): boolean => {
  const { permissions } = useDriverPermissions();
  return permissions.includes(permissionKey);
};
